/**
 * Employee Use Cases Factory
 *
 * This file CAN import from infrastructure — the composition root is the only
 * place where dependencies are assembled.
 *
 * @module @personnel/composition/factories/createEmployeeUseCases
 */

import type { AxiosInstance } from 'axios';
import type { EmployeeUseCases } from '@personnel/composition/types';

import { createEmployeeHttpClient } from '@personnel/infrastructure/api/EmployeeHttpClient';
import { createEmployeeApiMapper } from '@personnel/infrastructure/mappers/EmployeeApiMapper';
import { createEmployeeApiRepository } from '@personnel/infrastructure/repositories/EmployeeApiRepository';

import { getEmployeesUseCase } from '@personnel/application/use-cases/GetEmployeesUseCase';
import { getEmployeeByUuidUseCase } from '@personnel/application/use-cases/GetEmployeeByUuidUseCase';
import { createEmployeeUseCase } from '@personnel/application/use-cases/CreateEmployeeUseCase';
import { updateEmployeeUseCase } from '@personnel/application/use-cases/UpdateEmployeeUseCase';
import { deleteEmployeeUseCase } from '@personnel/application/use-cases/DeleteEmployeeUseCase';
import { bulkDeleteEmployeesUseCase } from '@personnel/application/use-cases/BulkDeleteEmployeesUseCase';
import { generateEmployeePinUseCase } from '@personnel/application/use-cases/GenerateEmployeePinUseCase';
import { validateEmployeePinUseCase } from '@personnel/application/use-cases/ValidateEmployeePinUseCase';

/**
 * Assembles all employee use cases with their injected dependencies.
 * Called ONCE during composition initialization.
 *
 * @param httpClient - Axios instance for the connect API
 * @returns Fully composed employee use cases
 */
export const createEmployeeUseCases = (httpClient: AxiosInstance): EmployeeUseCases => {
  const employeeHttpClient = createEmployeeHttpClient({ httpClient });
  const mapper = createEmployeeApiMapper();
  const apiPort = createEmployeeApiRepository({ httpClient: employeeHttpClient, mapper });

  return {
    getEmployees: getEmployeesUseCase({ apiPort }),
    getByUuid: getEmployeeByUuidUseCase({ apiPort }),
    create: createEmployeeUseCase({ apiPort }),
    update: updateEmployeeUseCase({ apiPort }),
    delete: deleteEmployeeUseCase({ apiPort }),
    bulkDelete: bulkDeleteEmployeesUseCase({ apiPort }),
    generatePin: generateEmployeePinUseCase({ apiPort }),
    validatePin: validateEmployeePinUseCase({ apiPort }),
  };
};
